import { useCallback, useEffect, useRef, useState } from 'react';
import coursesApi from 'Api/axios/courses-api';
import { COURSE_STATUS_PENDING } from 'Constants/General';
import type { CourseEmployeeForApprovalI } from 'types';

export interface EligibleEmployeeRow {
  id: string; // userCourse.id (DataGrid usa este como rowId)
  userId: string;
  payrollNum: number;
  fullName: string;
  department: string;
  jobTitle: string;
  terminal: string;
}

interface Props {
  courseId: string | null;
  enabled: boolean; // sólo fetcha cuando el panel está expandido
  page: number; // 0-indexed (DataGrid v5)
  pageSize: number;
  fullName?: string; // ya debounced por el caller
}

const mapToRow = (item: CourseEmployeeForApprovalI): EligibleEmployeeRow => ({
  id: item.id,
  userId: item.User.id,
  payrollNum: item.User.payrollNum,
  fullName: item.User.fullName,
  department: item.User.Department?.name ?? '',
  jobTitle: item.User.JobTitle?.name ?? '',
  terminal: item.User.Terminal?.name ?? '',
});

const useEligibleEmployeesForApproval = ({ courseId, enabled, page, pageSize, fullName }: Props) => {
  const [rows, setRows] = useState<EligibleEmployeeRow[]>([]);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Token para descartar respuestas obsoletas si los params cambian antes de que resuelva un fetch en vuelo.
  const requestTokenRef = useRef(0);

  const fetch = useCallback(async () => {
    if (!courseId) return;

    const token = ++requestTokenRef.current;
    setLoading(true);

    try {
      const res = await coursesApi.readCourseEmployeesForApproval(courseId, {
        page: page + 1, // backend es 1-indexed
        pageSize,
        userCourseStatusId: COURSE_STATUS_PENDING,
        ...(fullName ? { fullName } : {}),
      });

      // Si hubo otro fetch posterior, descartar este resultado.
      if (token !== requestTokenRef.current) return;

      setError(res.error ?? null);
      setRows((res.data ?? []).map(mapToRow));
      setTotalEmployees(res.pagination?.totalItems ?? 0);
    } catch (e) {
      if (token !== requestTokenRef.current) return;
      const message = e instanceof Error ? e.message : 'No se pudo cargar la lista de empleados';
      setError(message);
      setRows([]);
      setTotalEmployees(0);
    } finally {
      if (token === requestTokenRef.current) {
        setLoading(false);
      }
    }
  }, [courseId, page, pageSize, fullName]);

  useEffect(() => {
    if (!enabled) return;
    fetch();
  }, [enabled, fetch]);

  return { rows, totalEmployees, loading, error, refetch: fetch };
};

export default useEligibleEmployeesForApproval;
